import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Utensils } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklyReport = ({ token }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/analytics/weekly-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center p-4 text-gray-500 dark:text-gray-400">Loading report...</div>;
  }

  if (!report) {
    return <div className="text-center p-4 text-gray-500 dark:text-gray-400">No data available</div>;
  }

  const macroData = [
    { name: 'Protein', value: Math.round(report.total_protein), color: '#ef4444' },
    { name: 'Carbs', value: Math.round(report.total_carbs), color: '#f59e0b' },
    { name: 'Fats', value: Math.round(report.total_fats), color: '#eab308' }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="weekly-report">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <TrendingUp className="text-[#FF6600]" size={20} />
        Weekly Report
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="font-bold text-[#FF6600]">{report.total_meals}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Meals Logged</div>
        </div>
        <div className="text-center">
          <div className="font-bold text-[#FF6600]">{Math.round(report.total_calories)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Total Calories</div>
        </div>
        <div className="text-center">
          <div className="font-bold text-[#FF6600]">{Math.round(report.avg_calories_per_day)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Avg/Day</div>
        </div>
        <div className="text-center">
          <div className="font-bold text-[#FF6600]">{Math.round(report.total_protein)}g</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Protein</div>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="font-bold mb-4">Macronutrient Breakdown</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={macroData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#FF6600" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {report.most_eaten_foods && report.most_eaten_foods.length > 0 && (
        <div>
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Utensils size={16} />
            Most Eaten Foods
          </h3>
          <div className="space-y-2">
            {report.most_eaten_foods.map((food, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
                <span className="text-sm">{food.name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{food.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyReport;
